// Unpublished Work © 2023-2024 Deere & Company.

import { useOktaAuth } from "@okta/okta-react";
import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { axios } from "../../api";
import { Account } from "../../api/dealer/DealerApi";
import usePreferencesApi from "../../api/preferences/usePreferencesApi";
import { BusinessAction, RBACRole } from "../../api/rbac/types";
import useXtremeBff from "../../api/xtremeBff/useXtremeBff";
import xtremeBffApi from "../../api/xtremeBff/xtremeBffApi";
import { UserPreferenceResponse } from "../../api/xtremeBff/xtremeBffInterfaces";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import {
  MasterPreference,
  Preference,
  UpdatedPreference,
} from "../../pages/preferences/types";
import {
  getDealerAccountFromLocation,
  isAuthorizedForMultipleAccounts,
} from "../../pages/preferences/utils";
import { ConfigContext } from "../ConfigContext";
import { DealerAccountContext } from "../DealerAccountContext";
import { useEffectiveUserId } from "../DelegateContext/DelegateContext";
import { RBACAccessContext } from "../RBACAccessContext/RBACAccessContext";
import { UserContext, UserDetails } from "../UserContext/UserContext";

export const DEFAULT_MASTER_PREFERENCES: MasterPreference[] = [
  {
    key: "language",
    value: "en_US",
    options: [
      "en_US",
      "es_MX",
      "pt_BR",
      "zh_CN",
      "fr_CA",
      "de_DE",
      "it_IT",
      "pl_PL",
    ],
    level: "user",
    userOverridable: true,
  },
  {
    key: "unit_of_measurement",
    value: "metric",
    options: [],
    level: "user",
    userOverridable: true,
  },
  {
    key: "application_style",
    value: "deere_green",
    options: [],
    level: "user",
    userOverridable: true,
  },
];

export const DEFAULT_USER_PREFERENCES: Preference[] =
  DEFAULT_MASTER_PREFERENCES.map(
    ({ key, value, userOverridable }) =>
      ({
        key,
        value,
        userOverridable,
      } as Preference)
  );

export interface AppPreference {
  masterPreferences: MasterPreference[];
  accountPreferences: Preference[];
  userPreferences: Preference[];
  saveUserPreferences: (delta: UpdatedPreference[]) => Promise<void>;
  saveAccountPreferences?: (delta: Preference[]) => Promise<void>;
  isErrorOccurred: boolean;
  reloadPreferences: () => void;
  reloadAccountPreferences: () => Promise<void>;
  defaultAccountExpired: boolean;
}

const fetchMasterPreferences = (
  preferencesServiceUrl: string,
  accessToken: string
): Promise<MasterPreference[]> =>
  axios
    .getWithRetries<{
      preferences: MasterPreference[];
    }>(`${preferencesServiceUrl}/preferences/v1/master`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    .then(({ data: { preferences } }) => preferences);

const fetchAccountPreferences = (
  preferencesServiceUrl: string,
  dealerAccountId: string,
  accessToken: string
): Promise<Preference[]> =>
  axios
    .getWithRetries<{ preferences: Preference[] }>(
      `${preferencesServiceUrl}/preferences/v1/account/${dealerAccountId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
    .then(({ data: { preferences } }) => preferences);

const fetchUserPreferencesFromBff = (
  xtremeBffServiceUrl: string,
  accessToken: string,
  userXid: string,
  dealerAccountId?: string | undefined,
  mappedAccountIds?: string[],
  enableEmptyMappingAccountHandling?: boolean,
  isApiAuthEnabled?: boolean,
  xtremeBffApiHook?: ReturnType<typeof useXtremeBff>
): Promise<UserPreferenceResponse> => {
  if (enableEmptyMappingAccountHandling && mappedAccountIds?.length === 0) {
    return Promise.reject(Error("No mapped accounts available for the user"));
  }
  if (isApiAuthEnabled) {
    if (!xtremeBffApiHook) {
      return Promise.reject(
        Error("Xtreme BFF client is unavailable while API auth is enabled")
      );
    }
    return xtremeBffApiHook
      .getUserPreferences(userXid, dealerAccountId, mappedAccountIds)
      .then((preferences) => preferences);
  }
  return xtremeBffApi
    .getUserPreferences(
      xtremeBffServiceUrl,
      accessToken,
      userXid,
      dealerAccountId,
      mappedAccountIds
    )
    .then((preferences) => preferences);
};

const fetchUserPreferences = (
  preferencesServiceUrl: string,
  userXid: string,
  dealerAccountId: string | undefined,
  accessToken: string
): Promise<Preference[]> =>
  axios
    .getWithRetries<{ preferences: Preference[] }>(
      dealerAccountId
        ? `${preferencesServiceUrl}/preferences/v1/user/${userXid}?accountId=${dealerAccountId}`
        : `${preferencesServiceUrl}/preferences/v1/user/${userXid}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
    .then(({ data: { preferences } }) => preferences);

export const fetchAllPreferences = async (
  preferenceServiceUrl: string,
  xtremeBffServiceUrl: string,
  isAccountPreferencesEnabled: boolean,
  callPreferencesApiFromBff: boolean,
  mappedAccounts: string[] | undefined,
  dealerAccountId: string | undefined,
  userId: string,
  enableAccountSelectionOptions: boolean | undefined,
  accessToken: string,
  enableEmptyMappingAccountHandling: boolean,
  isApiAuthEnabled: boolean,
  usePreferencesApiHook: ReturnType<typeof usePreferencesApi>,
  xtremeBffApiHook: ReturnType<typeof useXtremeBff>
): Promise<
  Pick<
    AppPreference,
    | "masterPreferences"
    | "accountPreferences"
    | "userPreferences"
    | "defaultAccountExpired"
  >
> => {
  function getMasterPrefPromise() {
    return isApiAuthEnabled
      ? usePreferencesApiHook.fetchMasterPreferences()
      : fetchMasterPreferences(preferenceServiceUrl, accessToken);
  }

  const masterPrefPromise = getMasterPrefPromise();
  const accPrefPromise = (() => {
    if (!isAccountPreferencesEnabled || !dealerAccountId) {
      return Promise.resolve([]);
    }
    if (isApiAuthEnabled) {
      return usePreferencesApiHook.fetchAccountPreferences(dealerAccountId);
    }
    return fetchAccountPreferences(
      preferenceServiceUrl,
      dealerAccountId,
      accessToken
    );
  })();

  const userPrefPromise =
    callPreferencesApiFromBff && enableAccountSelectionOptions
      ? await fetchUserPreferencesFromBff(
          xtremeBffServiceUrl,
          accessToken,
          userId,
          dealerAccountId,
          mappedAccounts,
          enableEmptyMappingAccountHandling,
          isApiAuthEnabled,
          xtremeBffApiHook
        )
      : await fetchUserPreferencesFromAPI(
          preferenceServiceUrl,
          userId,
          dealerAccountId,
          accessToken,
          isApiAuthEnabled,
          usePreferencesApiHook
        );

  const responses = await Promise.allSettled([
    masterPrefPromise,
    accPrefPromise,
    userPrefPromise,
  ]);
  const resolvedResponses = responses.filter(
    (r) => r.status === "fulfilled"
  ) as [
    PromiseFulfilledResult<MasterPreference[]>,
    PromiseFulfilledResult<Preference[]>,
    PromiseFulfilledResult<UserPreferenceResponse>
  ];

  if (resolvedResponses.length !== responses.length)
    throw new Error("Error loading preferences");

  const [masterPrefResponse, accountPrefResponse, userPrefResponse] =
    resolvedResponses;

  return {
    masterPreferences: masterPrefResponse.value,
    accountPreferences: accountPrefResponse.value,
    userPreferences: userPrefResponse.value.preferences,
    defaultAccountExpired: userPrefResponse.value.defaultAccountExpired,
  };
};

const fetchUserPreferencesFromAPI = (
  preferenceServiceUrl: string,
  userId: string,
  dealerAccountId: string | undefined,
  accessToken: string,
  isApiAuthEnabled: boolean,
  usePreferencesApiHook: ReturnType<typeof usePreferencesApi>
): Promise<{
  preferences: Preference[];
  interestedAccountChanged: boolean;
  defaultAccountExpired: boolean;
}> => {
  if (isApiAuthEnabled) {
    return usePreferencesApiHook
      .fetchUserPreferences(userId, dealerAccountId)
      .then((preferences) => ({
        preferences,
        interestedAccountChanged: false,
        defaultAccountExpired: false,
      }));
  }
  return fetchUserPreferences(
    preferenceServiceUrl,
    userId,
    dealerAccountId,
    accessToken
  ).then((preferences) => ({
    preferences,
    interestedAccountChanged: false,
    defaultAccountExpired: false,
  }));
};

const saveAccountPreferences =
  (
    preferencesServiceUrl: string,
    dealerAccountId: string | undefined,
    accessToken: string,
    postSave: (delta: Preference[]) => Promise<void> | void
  ): ((delta: Preference[]) => Promise<void>) =>
  (delta: Preference[]): Promise<void> => {
    if (!dealerAccountId)
      return Promise.reject(new Error("Dealer account id is missing."));
    return axios
      .postWithRetries(
        `${preferencesServiceUrl}/preferences/v1/account/${dealerAccountId}`,
        { preferences: delta },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then(() => postSave(delta));
  };

const saveUserPreferences =
  (
    preferencesServiceUrl: string,
    dealerAccountId: string | undefined,
    userId: string,
    accessToken: string,
    postSave: (delta: UpdatedPreference[]) => Promise<void> | void
  ): ((delta: UpdatedPreference[]) => Promise<void>) =>
  (delta: UpdatedPreference[]): Promise<void> =>
    axios
      .postWithRetries(
        dealerAccountId
          ? `${preferencesServiceUrl}/preferences/v1/user/${userId}?accountId=${dealerAccountId}`
          : `${preferencesServiceUrl}/preferences/v1/user/${userId}`,
        { preferences: delta },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then(() => postSave(delta));

const detectAccountSelectionPreferencesChanges = (
  preferences: UpdatedPreference[],
  user: UserDetails | undefined,
  enableMultipleAccounts: boolean,
  primaryAccount: Account | undefined,
  enableRbacAuthSupportForCorporateView: boolean = false
): {
  changed: boolean;
  defaultAccount: string | undefined;
  interestedAccounts: string | undefined;
} => {
  const defaultAccountPreference = preferences.find(
    ({ key }) => key === "default_account"
  );
  const interestedAccountsPreferences = preferences.find(
    ({ key }) => key === "interested_accounts"
  );
  const isAuthorizedForAccountSelection = isAuthorizedForMultipleAccounts(
    user,
    enableMultipleAccounts,
    enableRbacAuthSupportForCorporateView
  );
  const defaultSelectedValueForNonCorporateRole =
    getDealerAccountFromLocation(primaryAccount);

  return {
    changed: !!defaultAccountPreference || !!interestedAccountsPreferences,
    defaultAccount:
      isAuthorizedForAccountSelection &&
      !user?.is(RBACRole.CORPORATE_PARTS_MANAGER)
        ? defaultSelectedValueForNonCorporateRole
        : defaultAccountPreference?.value,
    interestedAccounts: interestedAccountsPreferences?.value,
  };
};

export const PreferenceContext = createContext<AppPreference>({
  masterPreferences: [],
  accountPreferences: [],
  userPreferences: [],
  saveUserPreferences: () => Promise.resolve(),
  saveAccountPreferences: () => Promise.resolve(),
  isErrorOccurred: false,
  reloadPreferences: () => {},
  reloadAccountPreferences: () => Promise.resolve(),
  defaultAccountExpired: false,
});

export const PreferenceProvider = ({
  children,
  initialValue,
  isErrorOccurred: isErrorOccurredInInitialCall = false,
}: {
  children: ReactElement;
  initialValue?: any;
  isErrorOccurred?: boolean;
}) => {
  const {
    PREFERENCES_SERVICE_URL,
    USE_BFF_PREFERENCES_ENDPOINT,
    XTREME_BFF_SERVICE_URL,
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_EMPTY_MAPPING_ACCOUNT_HANDLING,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_PREFERENCES,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
  } = useContext(ConfigContext);
  const {
    updateMappedAccounts,
    selectedAccounts,
    dealerAccount,
    allAccounts,
    isErrorOccurred: isDealerErrorOccurred,
  } = useContext(DealerAccountContext);
  const { authState } = useOktaAuth();
  const effectiveUserId = useEffectiveUserId();
  const user = useContext(UserContext);
  const { isCorporateViewEnabled, isRBACEnabled, primaryAccount } =
    useContext(RBACAccessContext);

  const isApiAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_PREFERENCES;
  const usePreferencesApiHook = usePreferencesApi();
  const xtremeBffApiHook = useXtremeBff();

  const isAuthorizedToEditAndViewAccountPreferences =
    !isRBACEnabled ||
    (isRBACEnabled &&
      (user?.isAuthorizedTo(BusinessAction.VIEW_ACCOUNT_PREFERENCE) ||
        user?.isAuthorizedTo(BusinessAction.EDIT_ACCOUNT_PREFERENCE)));

  const isAuthorisedToEditAccountPreferences =
    !isRBACEnabled ||
    (isRBACEnabled &&
      user?.isAuthorizedTo(BusinessAction.EDIT_ACCOUNT_PREFERENCE));

  const [loading, setLoading] = useState(true);

  const accessToken = authState?.accessToken?.accessToken as string;

  const enableAccountSelectionOptions =
    isCorporateViewEnabled &&
    isAuthorizedForMultipleAccounts(
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    );

  const dealerAccountId = useMemo(() => {
    if (enableAccountSelectionOptions) {
      return selectedAccounts.length === 1 ? selectedAccounts[0].id : undefined;
    }
    return dealerAccount?.id;
  }, [selectedAccounts, dealerAccount]);

  const shouldLoadAccountPreferencesAgain =
    isRBACEnabled && user?.isUserWithJDRole() && dealerAccountId;

  const postPreferencesSaveAction = (
    delta: UpdatedPreference[]
  ): Promise<void> => {
    const { changed } = detectAccountSelectionPreferencesChanges(
      delta,
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      primaryAccount,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    );
    return fetchAllPreferences(
      PREFERENCES_SERVICE_URL,
      XTREME_BFF_SERVICE_URL,
      !!isAuthorizedToEditAndViewAccountPreferences,
      USE_BFF_PREFERENCES_ENDPOINT,
      user?.accounts?.map((account) => account.id),
      dealerAccountId,
      effectiveUserId,
      enableAccountSelectionOptions,
      accessToken,
      ENABLE_EMPTY_MAPPING_ACCOUNT_HANDLING,
      isApiAuthEnabled,
      usePreferencesApiHook,
      xtremeBffApiHook
    )
      .then((preferences) => {
        setAppPreferences((prevState) => {
          if (prevState === undefined) return undefined;
          return {
            masterPreferences: [...preferences.masterPreferences],
            accountPreferences: [...preferences.accountPreferences],
            userPreferences: [...preferences.userPreferences],
            saveAccountPreferences: prevState.saveAccountPreferences,
            saveUserPreferences: prevState.saveUserPreferences,
            isErrorOccurred: false,
            reloadPreferences: prevState.reloadPreferences,
            reloadAccountPreferences: loadAccountPreferences,
            defaultAccountExpired: preferences.defaultAccountExpired,
          };
        });
        if (changed && enableAccountSelectionOptions) {
          const corporatePreferences = preferences.userPreferences.filter(
            (preference) =>
              preference.key === "default_account" ||
              preference.key === "interested_accounts"
          );
          updateAccountsSelection(corporatePreferences ?? []);
        }
      })
      .catch(() =>
        setAppPreferences(
          (prevState) =>
            ({
              masterPreferences: DEFAULT_MASTER_PREFERENCES,
              accountPreferences: [] as Preference[],
              userPreferences: DEFAULT_USER_PREFERENCES,
              saveUserPreferences: prevState?.saveUserPreferences,
              saveAccountPreferences: prevState?.saveAccountPreferences,
              isErrorOccurred: true,
              reloadPreferences: prevState?.reloadPreferences,
            } as AppPreference)
        )
      )
      .then(() => undefined);
  };

  const saveUserPreferencesFn = useMemo(
    () =>
      isApiAuthEnabled
        ? usePreferencesApiHook.saveUserPreferences(
            dealerAccountId,
            effectiveUserId,
            postPreferencesSaveAction
          )
        : saveUserPreferences(
            PREFERENCES_SERVICE_URL,
            dealerAccountId,
            effectiveUserId,
            accessToken,
            postPreferencesSaveAction
          ),
    [dealerAccountId]
  );

  const saveAccountPreferencesFn = useMemo(
    () =>
      isApiAuthEnabled
        ? usePreferencesApiHook.saveAccountPreferences(
            dealerAccountId,
            postPreferencesSaveAction
          )
        : saveAccountPreferences(
            PREFERENCES_SERVICE_URL,
            dealerAccountId,
            accessToken,
            postPreferencesSaveAction
          ),
    [dealerAccountId]
  );

  const updateAccountsSelection = (preferences: Preference[]) => {
    const defaultAccountValueInAppPreferences =
      appPreferences?.userPreferences.find(
        (preference) => preference.key === "default_account"
      )?.value;
    const interestedAccountValueInAppPreferences =
      appPreferences?.userPreferences.find(
        (preference) => preference.key === "interested_accounts"
      )?.value;
    const {
      defaultAccount: defaultAccountValue,
      interestedAccounts: interestedAccountsValue,
    } = detectAccountSelectionPreferencesChanges(
      preferences,
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      primaryAccount,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    );

    if (defaultAccountValue !== "" || interestedAccountsValue !== "") {
      const defaultAccount = (
        defaultAccountValue !== undefined
          ? defaultAccountValue
          : defaultAccountValueInAppPreferences
      )?.split(" ")[0];
      const interestedAccounts = (
        interestedAccountsValue !== undefined
          ? interestedAccountsValue
          : interestedAccountValueInAppPreferences
      )
        ?.split(",")
        .map((accIdText) => accIdText.trim());
      const accountIds = [
        ...(defaultAccount ? [defaultAccount] : []),
        ...(interestedAccounts?.filter(
          (accId) => accId && accId !== defaultAccount
        ) ?? []),
      ];
      if (primaryAccount?.id && !accountIds.includes(primaryAccount.id)) {
        accountIds.push(primaryAccount.id);
      }
      if (!isDealerErrorOccurred) updateMappedAccounts(accountIds);
    }
  };

  const loadAccountPreferences = useCallback(() => {
    if (dealerAccountId === undefined) return Promise.resolve();

    return fetchAccountPreferences(
      PREFERENCES_SERVICE_URL,
      dealerAccountId as string,
      accessToken
    ).then((accPreferences) => {
      setAppPreferences((prevState) => {
        if (prevState === undefined) return prevState;
        return {
          ...prevState,
          accountPreferences: accPreferences,
        };
      });
    });
  }, [dealerAccountId]);

  const loadAllPreferences = () =>
    fetchAllPreferences(
      PREFERENCES_SERVICE_URL,
      XTREME_BFF_SERVICE_URL,
      !!isAuthorizedToEditAndViewAccountPreferences,
      USE_BFF_PREFERENCES_ENDPOINT,
      user?.accounts?.map((account) => account.id),
      dealerAccountId,
      effectiveUserId,
      enableAccountSelectionOptions,
      accessToken,
      ENABLE_EMPTY_MAPPING_ACCOUNT_HANDLING,
      isApiAuthEnabled,
      usePreferencesApiHook,
      xtremeBffApiHook
    )
      .then((preferences) => {
        setAppPreferences({
          ...preferences,
          saveUserPreferences: saveUserPreferencesFn,
          ...(isAuthorisedToEditAccountPreferences && {
            saveAccountPreferences: saveAccountPreferencesFn,
          }),
          isErrorOccurred: false,
          reloadPreferences: loadAllPreferences,
          reloadAccountPreferences: loadAccountPreferences,
          defaultAccountExpired: preferences.defaultAccountExpired,
        });
      })
      .catch(() => {
        setAppPreferences({
          masterPreferences: DEFAULT_MASTER_PREFERENCES,
          accountPreferences: [] as Preference[],
          userPreferences: DEFAULT_USER_PREFERENCES,
          ...(isAuthorisedToEditAccountPreferences && {
            saveAccountPreferences: saveAccountPreferencesFn,
          }),
          saveUserPreferences: saveUserPreferencesFn,
          isErrorOccurred: true,
          reloadPreferences: loadAllPreferences,
          reloadAccountPreferences: loadAccountPreferences,
          defaultAccountExpired: false,
        });
      });

  const initialAppPreferences: AppPreference = {
    ...(initialValue ?? {}),
    saveUserPreferences: saveUserPreferencesFn,
    ...(isAuthorisedToEditAccountPreferences && {
      saveAccountPreferences: saveAccountPreferencesFn,
    }),
    isErrorOccurred: isErrorOccurredInInitialCall,
    reloadPreferences: loadAllPreferences,
    reloadAccountPreferences: loadAccountPreferences,
    ...((isErrorOccurredInInitialCall || isDealerErrorOccurred) && {
      masterPreferences: DEFAULT_MASTER_PREFERENCES,
      accountPreferences: [] as Preference[],
      userPreferences: DEFAULT_USER_PREFERENCES,
    }),
  };
  const [appPreferences, setAppPreferences] = useState<
    AppPreference | undefined
  >(
    initialValue === undefined && !isErrorOccurredInInitialCall
      ? undefined
      : initialAppPreferences
  );

  useEffect(() => {
    if (
      authState &&
      authState.isAuthenticated &&
      accessToken &&
      (!initialValue || shouldLoadAccountPreferencesAgain) &&
      !isErrorOccurredInInitialCall
    ) {
      loadAllPreferences();
    }
  }, [authState, initialValue, isErrorOccurredInInitialCall, selectedAccounts]);

  useEffect(() => {
    if (appPreferences?.userPreferences) {
      if (selectedAccounts?.length > 0 || allAccounts?.length > 0) {
        setLoading(false);
        return;
      }

      const { defaultAccount: defaultAccountInUserPreferences } =
        detectAccountSelectionPreferencesChanges(
          appPreferences.userPreferences,
          user,
          ENABLE_MULTIPLE_ACCOUNTS,
          primaryAccount,
          ENABLE_CORPORATE_VIEW_DPA_ROLE
        );
      const isDefaultAccountPresent =
        (user && user?.is(RBACRole.CORPORATE_PARTS_MANAGER)) ||
        !ENABLE_MULTIPLE_ACCOUNTS
          ? defaultAccountInUserPreferences
          : true;
      if (enableAccountSelectionOptions && isDefaultAccountPresent) {
        updateAccountsSelection(appPreferences.userPreferences);
        return;
      }
      setLoading(false);
    }
  }, [appPreferences?.userPreferences]);

  if (isDealerErrorOccurred)
    return (
      <PreferenceContext.Provider value={initialAppPreferences}>
        {children}
      </PreferenceContext.Provider>
    );

  if (appPreferences === undefined || loading)
    return <LoadingIndicator color="#367c2b" />;

  return (
    <PreferenceContext.Provider value={appPreferences}>
      {children}
    </PreferenceContext.Provider>
  );
};
